import { Component, DestroyRef, ElementRef, inject, input, output, signal } from '@angular/core';

import { createFloatingPanelDismiss } from '../../shared/overlay/floating-panel-dismiss';
import type { FloatingPanelDismiss } from '../../shared/overlay/floating-panel-dismiss';

import type { StockOptInColumnId } from './stock-columns';

@Component({
  selector: 'app-stock-column-picker',
  standalone: true,
  imports: [],
  template: `
    <div class="stock-column-picker">
      <button
        type="button"
        class="stock-column-picker__trigger app-action app-action--secondary"
        (click)="onTriggerClick($event)"
      >
        Columnas ({{ selected().size }}/{{ options().length }})
      </button>

      @if (isOpen()) {
        <div
          class="stock-column-picker__panel stock-column-picker__panel--floating"
          [style.top.px]="position()?.top"
          [style.left.px]="position()?.left"
          (click)="$event.stopPropagation()"
        >
          @for (option of options(); track option.id) {
            <label class="app-check-option">
              <input
                type="checkbox"
                [checked]="selected().has(option.id)"
                (change)="onCheckboxChange(option.id, $event)"
              />
              <span>{{ option.label }}</span>
            </label>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      .stock-column-picker {
        position: relative;
        display: inline-block;
      }

      .stock-column-picker__trigger {
        /* composes app-action app-action--secondary in template */
      }

      .stock-column-picker__panel--floating {
        display: flex;
        flex-direction: column;
        position: fixed;
        background-color: var(--surface-card, #fff);
        border: 1px solid var(--border-color, #ccc);
        border-radius: var(--radius-md, 4px);
        box-shadow: var(--shadow-md, 0 4px 6px rgba(0, 0, 0, 0.1));
        z-index: 100;
        padding: 0.5rem;
        gap: 0.25rem;
        min-width: 150px;
      }
      /* Option/checkbox styling lives in the shared .app-check-option rules
         in styles.css (openspec stock-toolbar-panel-dismiss, task 5.2). */
    `,
  ],
})
export class StockColumnPickerComponent {
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly selected = input.required<Set<StockOptInColumnId>>();
  readonly options = input.required<ReadonlyArray<{ id: StockOptInColumnId; label: string }>>();
  readonly visibilityChange = output<Set<StockOptInColumnId>>();

  readonly isOpen = signal(false);
  readonly position = signal<{ top: number; left: number } | null>(null);

  private readonly dismiss: FloatingPanelDismiss;

  constructor() {
    this.dismiss = createFloatingPanelDismiss({
      getHost: () => this.elementRef.nativeElement,
      getPanel: () => this.elementRef.nativeElement.querySelector('.stock-column-picker__panel'),
      close: () => this.closePanel(),
    });
    // The helper owns no lifecycle of its own: if the component goes away while
    // the panel is open, the document listeners must go with it.
    this.destroyRef.onDestroy(() => this.detachDismissListeners());
  }

  onTriggerClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isOpen()) {
      this.closePanel();
      return;
    }
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    this.position.set({ top: rect.bottom + 4, left: rect.left });
    this.isOpen.set(true);
    this.attachDismissListeners();
  }

  onCheckboxChange(id: StockOptInColumnId, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const next = new Set(this.selected());
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.visibilityChange.emit(next);
  }

  /** Close on a document `click` outside the host (public close helper). */
  onDocumentClick(event: MouseEvent): void {
    if (this.elementRef.nativeElement.contains(event.target as Node)) {
      return;
    }
    this.closePanel();
  }

  /** Close on Escape (public close helper). */
  onEscape(): void {
    this.closePanel();
  }

  private closePanel(): void {
    this.isOpen.set(false);
    this.detachDismissListeners();
  }

  private attachDismissListeners(): void {
    if (typeof document === 'undefined') {
      return; // DOM-less unit harness: there is no document to listen on.
    }
    this.dismiss.attach();
  }

  private detachDismissListeners(): void {
    if (typeof document === 'undefined') {
      return; // Nothing was ever attached in a DOM-less unit harness.
    }
    this.dismiss.detach();
  }
}
